import { useEffect, useRef, useState } from "preact/hooks";
import type { UIMessage } from "../types";

interface MessageListProps {
  messages: UIMessage[];
  pendingAssistant?: string;
  toolActivity?: string | null;
}

const STICKY_THRESHOLD_PX = 80;

export function MessageList({
  messages,
  pendingAssistant,
  toolActivity,
}: MessageListProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Explicit "stick to bottom" state. Starts true so first-render and
  // first-greeting-arrival both auto-scroll. Flipped off when the user
  // actively scrolls away from the bottom, flipped back on when they
  // scroll back within STICKY_THRESHOLD_PX. The previous version
  // inferred this from scrollTop == 0, which collapsed "user scrolled
  // to top" with "we haven't scrolled yet" — so the first overflowing
  // render silently skipped the auto-scroll.
  const [stickToBottom, setStickToBottom] = useState(true);

  const handleScroll = () => {
    const el = ref.current;
    if (!el) return;
    const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setStickToBottom(fromBottom < STICKY_THRESHOLD_PX);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el || !stickToBottom) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, pendingAssistant, toolActivity, stickToBottom]);

  return (
    <div class="nx-messages" ref={ref} onScroll={handleScroll}>
      {messages.map((m) => (
        <div key={m.id} class={`nx-message nx-message--${m.role}`}>
          <div class="nx-message__bubble">{m.content}</div>
        </div>
      ))}
      {toolActivity && (
        <div class="nx-tool-activity">{formatToolLabel(toolActivity)}…</div>
      )}
      {pendingAssistant && (
        <div class="nx-message nx-message--assistant">
          <div class="nx-message__bubble">
            {pendingAssistant}
            <span class="nx-cursor" aria-hidden="true">▌</span>
          </div>
        </div>
      )}
    </div>
  );
}

function formatToolLabel(toolName: string): string {
  // Best-effort prettifier: send_support_email → "Sending support email"
  const verb = toolName.startsWith("send_")
    ? "Sending"
    : toolName.startsWith("check_")
      ? "Checking"
      : toolName.startsWith("book_")
        ? "Booking"
        : toolName.startsWith("cancel_")
          ? "Cancelling"
          : toolName.startsWith("reschedule_")
            ? "Rescheduling"
            : "Running";
  const rest = toolName
    .replace(/^[a-z]+_/, "")
    .replace(/_/g, " ");
  return rest ? `${verb} ${rest}` : verb;
}
